import { useDataProvider, useNotify, useCreate } from "react-admin";
import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemButton,
} from "@mui/material";

export const AUTHOR_LIST_ROW_HEIGHT = 40; // px
export const AUTHOR_LIST_MIN_ROWS = 10;

interface Author {
  id: number;
  fullName: string;
  code: string;
}

interface AuthorSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (author: Author) => void;
  onCreate: (author: Author) => void;
}

export const AuthorSelectModal = ({
  open,
  onClose,
  onSelect,
  onCreate,
}: AuthorSelectModalProps) => {
  const dataProvider = useDataProvider();
  const [search, setSearch] = useState("");
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("people", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "fullName", order: "ASC" },
        filter: { type: "AUTHOR", fullName_like: search },
      })
      .then(({ data }) => {
        setAuthors(data as Author[]);
        setLoading(false);
      });
  }, [search, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        Select Author
        <Button
          variant="outlined"
          onClick={() => setShowCreate(true)}
          sx={{
            position: "absolute",
            top: 8,
            right: 16,
            zIndex: 2,
            whiteSpace: "nowrap",
            minHeight: 32,
          }}
        >
          CREATE NEW AUTHOR
        </Button>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Search author by name"
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                fullWidth
              />
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {authors.length > 0
                    ? authors.map((author: Author) => (
                        <ListItem
                          disablePadding
                          key={author.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(author);
                              onClose();
                            }}
                          >
                            <ListItemText primary={author.fullName} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No authors found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <AuthorCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(author) => {
                  setShowCreate(false);
                  onSelect(author as Author);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

interface AuthorCreateInlineProps {
  onClose: () => void;
  onCreated: (author: Author) => void;
}

const AuthorCreateInline = ({
  onClose,
  onCreated,
}: AuthorCreateInlineProps) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    type: "AUTHOR",
    lastName: "",
    firstName: "",
    middleName: "",
    fullName: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const fullName =
        `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();
      create(
        "people",
        { data: { ...formData, fullName } },
        {
          onSuccess: (data) => {
            notify("Author created successfully");
            if (data && data.id) {
              onCreated(data as Author);
            } else {
              notify("Error: Author creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error) => {
            console.error("Create error:", error);
            notify("Error creating author", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="First Name"
        value={formData.firstName}
        onChange={(e) =>
          setFormData({ ...formData, firstName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Middle Name"
        value={formData.middleName}
        onChange={(e) =>
          setFormData({ ...formData, middleName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

// SupervisorSelectModal is similar to AuthorSelectModal but with type: SUPERVISOR
export const SupervisorSelectModal = ({
  open,
  onClose,
  onSelect,
  onCreate,
}: AuthorSelectModalProps) => {
  const dataProvider = useDataProvider();
  const [search, setSearch] = useState("");
  const [supervisors, setSupervisors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("people", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "fullName", order: "ASC" },
        filter: { type: "SUPERVISOR", fullName_like: search },
      })
      .then(({ data }) => {
        setSupervisors(data as Author[]);
        setLoading(false);
      });
  }, [search, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        Select Supervisor
        <Button
          variant="outlined"
          onClick={() => setShowCreate(true)}
          sx={{
            position: "absolute",
            top: 8,
            right: 16,
            zIndex: 2,
            whiteSpace: "nowrap",
            minHeight: 32,
          }}
        >
          CREATE NEW SUPERVISOR
        </Button>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Search supervisor by name"
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                fullWidth
              />
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {supervisors.length > 0
                    ? supervisors.map((supervisor: Author) => (
                        <ListItem
                          disablePadding
                          key={supervisor.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={(supervisor: any) => {
                              onSelect(supervisor);
                              onClose();
                            }}
                          >
                            <ListItemText primary={supervisor.fullName} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No supervisors found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <SupervisorCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(supervisor) => {
                  setShowCreate(false);
                  onSelect(supervisor as Author);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const SupervisorCreateInline = ({
  onClose,
  onCreated,
}: AuthorCreateInlineProps) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    type: "SUPERVISOR",
    lastName: "",
    firstName: "",
    middleName: "",
    fullName: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const fullName =
        `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();
      create(
        "people",
        { data: { ...formData, fullName } },
        {
          onSuccess: (data) => {
            notify("Supervisor created successfully");
            if (data && data.id) {
              onCreated(data as Author);
            } else {
              notify("Error: Supervisor creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error) => {
            console.error("Create error:", error);
            notify("Error creating supervisor", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="First Name"
        value={formData.firstName}
        onChange={(e) =>
          setFormData({ ...formData, firstName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Middle Name"
        value={formData.middleName}
        onChange={(e) =>
          setFormData({ ...formData, middleName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Type"
        value="SUPERVISOR"
        disabled
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

// SpecialtySelectModal
export const SpecialtySelectModal = ({
  open,
  onClose,
  onSelect,
  onCreate,
}: any) => {
  const dataProvider = useDataProvider();
  const [search, setSearch] = useState("");
  const [specialties, setSpecialties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("specialties", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "name", order: "ASC" },
        filter: { name: search },
      })
      .then(({ data }) => {
        setSpecialties(data as any[]);
        setLoading(false);
      });
  }, [search, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Specialty</DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Search specialty by name"
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                fullWidth
              />
              <Button variant="outlined" onClick={() => setShowCreate(true)}>
                Create New Specialty
              </Button>
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {specialties.length > 0
                    ? specialties.map((specialty: any) => (
                        <ListItem
                          disablePadding
                          key={specialty.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(specialty);
                              onClose();
                            }}
                          >
                            <ListItemText
                              primary={
                                specialty.comment
                                  ? `${specialty.name} — ${specialty.comment}`
                                  : specialty.name
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No specialties found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <SpecialtyCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(specialty: any) => {
                  setShowCreate(false);
                  onSelect(specialty);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const SpecialtyCreateInline = ({ onClose, onCreated }: any) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    comment: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      create(
        "specialties",
        { data: formData },
        {
          onSuccess: (data: any) => {
            notify("Specialty created successfully");
            if (data && data.id) {
              onCreated(data);
            } else {
              notify("Error: Specialty creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error: any) => {
            console.error("Create error:", error);
            notify("Error creating specialty", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Comment"
        value={formData.comment}
        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

// UdcSelectModal
export const UdcSelectModal = ({ open, onClose, onSelect, onCreate }: any) => {
  const dataProvider = useDataProvider();
  const [search, setSearch] = useState("");
  const [udcs, setUdcs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("udc", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "name", order: "ASC" },
        filter: { name: search },
      })
      .then(({ data }) => {
        setUdcs(data as any[]);
        setLoading(false);
      });
  }, [search, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select UDC</DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Search UDC by name"
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                fullWidth
              />
              <Button variant="outlined" onClick={() => setShowCreate(true)}>
                Create New UDC
              </Button>
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {udcs.length > 0
                    ? udcs.map((udc: any) => (
                        <ListItem
                          disablePadding
                          key={udc.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(udc);
                              onClose();
                            }}
                          >
                            <ListItemText
                              primary={
                                udc.description
                                  ? `${udc.name} — ${udc.description}`
                                  : udc.name
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No UDCs found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <UdcCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(udc: any) => {
                  setShowCreate(false);
                  onSelect(udc);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const UdcCreateInline = ({ onClose, onCreated }: any) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      create(
        "udc",
        { data: formData },
        {
          onSuccess: (data: any) => {
            notify("UDC created successfully");
            if (data && data.id) {
              onCreated(data);
            } else {
              notify("Error: UDC creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error: any) => {
            console.error("Create error:", error);
            notify("Error creating UDC", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Description"
        value={formData.description}
        onChange={(e) =>
          setFormData({ ...formData, description: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};
